import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const sessionId = `klc-${Math.random().toString(36).slice(2)}`;

const GREETING = {
  role: "assistant",
  content:
    "Hello — I'm the KLC assistant. I can tell you about our practice areas, engagement model, or help you book a free consultation. How can I help?",
};

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);

    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = JSON.parse(line.slice(6));
          if (data.delta) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + data.delta };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I'm having trouble right now. Please email kambojlawchambers@outlook.in." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 2.2, type: "spring" }}
        onClick={() => setOpen(!open)}
        data-testid="chatbot-toggle"
        className="fixed bottom-6 right-6 z-[90] w-14 h-14 rounded-full bg-[#D95A41] text-[#F2EFE9] flex items-center justify-center shadow-2xl hover:bg-[#E6735A] transition-colors"
        aria-label="Open chat assistant"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X size={24} />
            </motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}>
              <MessageSquare size={24} />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-[90] w-[calc(100vw-3rem)] max-w-sm h-[520px] rounded-3xl bg-[#0B132B]/80 backdrop-blur-xl border border-white/10 flex flex-col overflow-hidden shadow-2xl"
            data-testid="chatbot-panel"
          >
            <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-[#D95A41] flex items-center justify-center text-[#F2EFE9]"><MessageSquare size={16} /></span>
              <div>
                <p className="font-700 text-[#F2EFE9] text-sm">KLC Assistant</p>
                <p className="text-xs text-[#94A3B8]">Kamboj Law Chambers</p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chatbot-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    m.role === "user" ? "bg-[#D95A41] text-[#F2EFE9] rounded-br-sm" : "bg-white/10 text-[#F2EFE9] rounded-bl-sm"
                  }`}>
                    {m.content || <span className="opacity-60">…</span>}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>

            <div className="p-3 border-t border-white/10 flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about our services…"
                data-testid="chatbot-input"
                className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-[#F2EFE9] placeholder:text-[#94A3B8] outline-none focus:border-[#D95A41] transition-colors"
              />
              <button onClick={send} disabled={busy} data-testid="chatbot-send"
                className="w-10 h-10 rounded-full bg-[#D95A41] text-[#F2EFE9] flex items-center justify-center hover:bg-[#E6735A] transition-colors disabled:opacity-50 shrink-0">
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
